import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { AxiosResponse } from "axios";
import liff from "@line/liff";
import { Text, XStack, YStack } from "tamagui";
import api, { handleRequest } from "@services/axios/apiManager";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { getSendOtpErrorMessage, getVerifyOtpErrorMessage, readRemainingSeconds } from "@utils/otpError";
import { VOICE_OTP_CALLER_ID } from "@utils/func/voiceOtp";
import { kiosk } from "../kioskTheme";
import { useKiosk } from "../useKiosk";
import { OTP_RESEND_SECS } from "../otpPrefetch";
import { isMobilePhone } from "../phoneSetupPolicy";
import { checkoutOtpCardMode } from "../checkoutOtpPhone";
import { Card, SectionLabel } from "./ConfirmParts";
import CheckoutAgreement from "./CheckoutAgreement";

const EMPTY_CODE = ["", "", "", "", ""];

const NETWORK_MSG = "網路連線不穩，請再試一次";
/**
 * 驗證請求沒有 response（ERR_NETWORK、逾時）或 5xx：不是碼錯。
 * 常見於 iOS 語音來電後連線被切。不改 otpError.ts（其他註冊流程共用），判斷寫在這裡。
 */
const isTransientError = (e: unknown) => {
  const s = (e as { response?: { status?: number } } | undefined)?.response?.status;
  return !s || s >= 500;
};

/** 0912345678 → 0912-345-678（確認段顯示完整號碼，讓客戶看清楚驗證碼要寄去哪支）。 */
export const formatMobile = (p: string) => p.replace(/^(\d{4})(\d{3})(\d{3})$/, "$1-$2-$3");

/**
 * 結算頁內嵌的簡訊驗證卡（Kiosk 確認頁與瓦斯訂購結算頁共用）。
 *
 * 兩段式（2026-10-14 第三次拍板）：
 *  - 確認段（phase="confirm"）：帳號手機＋「修改號碼」＋同意使用者協議＋「傳送驗證碼」。
 *    **進頁不自動寄簡訊；沒勾協議不寄。**
 *  - 輸入驗證碼段（phase="code"）：按下「傳送驗證碼」後才寄；5 碼輸入、重寄倒數、改用語音。
 * 填滿 5 碼向後端驗證，成功後把 phoneVerifiedTicket 存進 useKiosk 並標記 otpVerified=true；
 * 呼叫端的送出按鈕以 otpVerified 為準。
 */
const OtpCard = ({
  code,
  setCode,
  phone,
  accountLoaded,
  alreadyVerified,
  onChangePhone,
  onVerified,
  noAccountPhone,
  verifiedNote,
}: {
  code: string[];
  setCode: (v: string[]) => void;
  phone: string;
  /**
   * 帳號資料是否已載入（分辨「號碼還沒回來」與「帳號號碼不是手機」）。
   * 載入後號碼不是手機（市話／非數字）→ 直接打開「修改號碼」。沒給時以 !!phone 代替。
   */
  accountLoaded?: boolean;
  /** 進頁時 otpVerified 已為 true（本 session 驗過）→ 直接顯示「驗證成功」，不出現協議、不寄。 */
  alreadyVerified?: boolean;
  /** 有傳才顯示「修改號碼」；送出的是只留數字、已過 isMobilePhone 的新號碼。呼叫端負責更新 phone。 */
  onChangePhone?: (phone: string) => void;
  /** 本卡驗證成功（呼叫端據此把號碼寫回帳號）。 */
  onVerified?: (phone: string, phoneVerifiedTicket: string) => void;
  /** 帳號沒有手機（設計稿 ①）：開在輸入模式、無取消、專用文案。 */
  noAccountPhone?: boolean;
  /** 驗證成功下方的提示（寫回帳號失敗時由呼叫端給）。 */
  verifiedNote?: string;
}) => {
  const { lineAccessToken } = useLineAuth();
  const { setOtpVerified, setPhoneVerifiedTicket, setOtpSent } = useKiosk();
  // 外部把本次驗證清掉（後端送單回「手機未驗證」，見 phoneNotVerified.ts）→ 卡片要回到確認段。
  // 只看「true → false」的轉變：掛載當下的值不算（呼叫端以 alreadyVerified 帶入初值）。
  const storeOtpVerified = useKiosk((s) => s.otpVerified);
  const prevStoreOtpVerified = useRef(storeOtpVerified);
  /**
   * 「最近是否已寄給同一支門號」——一律用 getState() 現讀，不可用 render 時解構的快照。
   * 用途：客戶離開結算頁再回來、或改號又改回來時，同一支號碼還在冷卻內就不重寄（真簡訊要錢）。
   */
  const recentlySentTo = (target: string) => {
    const digits = (target || "").replace(/\D/g, "");
    if (!digits) return 0;
    const { otpSentPhone, otpSentAt } = useKiosk.getState();
    if (otpSentPhone !== digits) return 0;
    const left = OTP_RESEND_SECS - Math.floor((Date.now() - otpSentAt) / 1000);
    return left > 0 ? left : 0;
  };
  const [phase, setPhase] = useState<"confirm" | "code">("confirm");
  const [agreed, setAgreed] = useState(false);
  const [agreeError, setAgreeError] = useState(false);
  const [secs, setSecs] = useState(0);
  // 語音冷卻與簡訊分開（後端兩個管道各自計冷卻）：簡訊冷卻中仍要能立刻改用語音。
  const [voiceSecs, setVoiceSecs] = useState(0);
  // 最後一次用哪個管道送出，只影響說明文案。
  const [lastChannel, setLastChannel] = useState<"sms" | "voice">("sms");
  // 語音外撥請求進行中（後端同步等 TwSMS 受理，可能很慢；沒有等待狀態使用者會狂按）。
  const [voiceSending, setVoiceSending] = useState(false);
  // 最近一次語音外撥失敗：標題不能再宣稱「將撥打電話」。
  const [voiceFailed, setVoiceFailed] = useState(false);
  const [verified, setVerified] = useState(!!alreadyVerified);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  // 修改號碼的編輯狀態
  const [editing, setEditing] = useState(false);
  const [draftPhone, setDraftPhone] = useState("");
  const [draftError, setDraftError] = useState("");
  /** 編輯框是卡片自己因 mode==="edit" 打開的（不是使用者按「修改號碼」）。 */
  const autoEditRef = useRef(false);
  // onVerified 用 ref 保存：父元件每次 render 的 callback 身分都不同，放進依賴會讓驗證重跑。
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const triedCode = useRef<string | null>(null);
  /** 這組碼已因網路錯誤自動重送過一次（碼被改動或重新進輸入段時重置）。 */
  const autoRetried = useRef(false);
  /** 遞增即重跑驗證 effect（網路錯誤的自動重送／「重新驗證」）。 */
  const [retryTick, setRetryTick] = useState(0);
  /** 目前畫面上的門號（非同步回應回來時判斷號碼是否已被換掉）。 */
  const phoneRef = useRef(phone);
  phoneRef.current = phone;
  /** 上一次看到的號碼：判斷「換號」（不論驗過沒）。 */
  const lastPhone = useRef(phone);
  /** 號碼是不是使用者在本卡「修改號碼」換的（只影響延後寄送的文案）。 */
  const changedHere = useRef(false);
  /** 編輯中直接按「傳送驗證碼」：等呼叫端把 phone 換成這支後才寄（見換號 effect）。 */
  const pendingSendTo = useRef<string | null>(null);
  /** pendingSendTo 要用哪個管道寄（簡訊／電話語音）。 */
  const pendingChannel = useRef<"sms" | "voice">("sms");
  /**
   * 延後寄送：寄送被後端 60 秒冷卻擋下（429，冷卻是 per lineUserId 不分號碼）時不顯示紅字，
   * 改倒數後自動再寄一次。常見情境：剛寄到帳號手機，馬上「修改號碼」再按傳送。
   */
  const [deferredSecs, setDeferredSecs] = useState(0);
  const deferredPhone = useRef<string | null>(null);
  const mode = checkoutOtpCardMode({ phone, accountLoaded });

  const sendOtp = async (targetPhone: string, manual = false, channel: "sms" | "voice" = "sms", deferredRetry = false) => {
    if (!targetPhone) return;
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    setError("");
    if (channel === "voice") setVoiceFailed(false);
    try {
      const res = await api.post<{ data?: { resendAvailableInSeconds?: number } }>(
        "/app-api/line/send-phone-otp",
        { accessToken: lineAccessToken, phone: targetPhone, channel },
        { isAuth: false },
      );
      setLastChannel(channel);
      // 只有簡訊記進 otpSent（recentlySentTo 讀它）；語音寫進去會讓簡訊被誤判為已寄過而跳過。
      if (channel === "sms") setOtpSent(targetPhone.replace(/\D/g, ""), Date.now());
      // 冷卻秒數以後端回傳為準；沒回才退回本地常數。
      const cooldown = res.data?.data?.resendAvailableInSeconds || OTP_RESEND_SECS;
      if (channel === "voice") setVoiceSecs(cooldown);
      else setSecs(cooldown);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: unknown } } } | undefined;
      const raw = err?.response?.data?.message;
      const msg = (Array.isArray(raw) ? raw.join(" ") : typeof raw === "string" ? raw : "").toLowerCase();
      // 429 只代表「這個 lineUserId 最近寄過一封」，不知道寄到哪支。
      // 只有我們自己記錄了「剛剛寄給同一支號碼」時，才當成良性重複、靜默顯示輸入框。
      const left = recentlySentTo(targetPhone);
      if (err?.response?.status === 429 && !msg.includes("daily") && left > 0 && !manual && channel === "sms") {
        setSecs(left);
        setError("");
        return;
      }
      // 號碼對不上上一封 → 照後端給的剩餘秒數倒數，到時自動寄到這支號碼（只延後一次）。
      const wait = err?.response?.status === 429 && !msg.includes("daily") ? readRemainingSeconds(e) : null;
      if (wait && !manual && !deferredRetry && channel === "sms") {
        if (targetPhone !== phoneRef.current) return;
        deferredPhone.current = targetPhone;
        setDeferredSecs(wait);
        setError("");
        return;
      }
      if (channel === "voice") setVoiceFailed(true);
      // 寄送失敗：讓「重新傳送」立刻可按（否則要白等 60 秒才能重試）。
      if (channel === "sms") setSecs(0);
      setError(getSendOtpErrorMessage(e));
    }
  };

  /** 「改用語音驗證」的包裝：只多管等待狀態，實際發送仍走 sendOtp。 */
  const sendVoiceOtp = async (target: string = phone) => {
    if (voiceSending) return;
    setVoiceSending(true);
    try {
      await sendOtp(target, true, "voice");
    } finally {
      setVoiceSending(false);
    }
  };

  /**
   * 切到輸入驗證碼段（只動畫面，不寄）。回傳這支號碼的冷卻剩餘秒數（>0 表示剛寄過、這次不重寄）。
   */
  const showCodePhase = (target: string, channel: "sms" | "voice" = "sms"): number => {
    // 語音不寄簡訊（兩個管道各自冷卻）：簡訊冷卻不計，「改用簡訊接收」要能立刻按。
    const left = channel === "voice" ? 0 : recentlySentTo(target);
    setCode(EMPTY_CODE);
    triedCode.current = null;
    autoRetried.current = false;
    setError("");
    setPhase("code");
    setSecs(channel === "voice" ? 0 : left > 0 ? left : OTP_RESEND_SECS);
    setLastChannel(channel);
    return left;
  };

  /**
   * 開始驗證：切到輸入驗證碼段並寄出。給 effect 用（編輯中按傳送、等呼叫端換好號碼後）。
   * 不是 manual：這次若撞冷卻，走上面的良性重複／延後補寄，不顯示紅字。
   */
  const startSend = (target: string, channel: "sms" | "voice" = "sms") => {
    if (showCodePhase(target, channel) > 0) return;
    if (channel === "voice") void sendVoiceOtp(target);
    else void sendOtp(target);
  };

  /** focus 第一格驗證碼。 */
  const focusFirstCodeInput = () => inputs.current[0]?.focus();

  /**
   * 按鈕專用的「開始驗證」：用 flushSync 把輸入格同步畫出來，在同一個點擊手勢內 focus 第一格。
   * iOS（LINE 內建瀏覽器是 WKWebView）只有在使用者手勢的同步流程內 focus() 才會彈出鍵盤，
   * setTimeout / effect 裡 focus 最多只拿到游標、不彈鍵盤。
   * 只能從點擊 handler 呼叫：flushSync 不可以在 effect 裡用。下方 60ms effect 保留當後備。
   */
  const startSendFromTap = (target: string, channel: "sms" | "voice" = "sms") => {
    let left = 0;
    flushSync(() => {
      left = showCodePhase(target, channel);
    });
    focusFirstCodeInput();
    if (left > 0) return;
    if (channel === "voice") void sendVoiceOtp(target);
    else void sendOtp(target);
  };

  /** 已通過驗證的門號：驗證只對這支號碼成立，換號就得重來。null = 還不知道是哪一支。 */
  const verifiedPhone = useRef<string | null>(alreadyVerified && phone ? phone : null);
  /** 這次驗證是不是「在本頁完成」的（相對於上一關帶進來的 alreadyVerified）。 */
  const verifiedHere = useRef(false);

  // 門號變動：
  //  1. 驗過的號碼被換掉 → 作廢驗證（元件自己守，不依賴呼叫端記得重置 store）。
  //  2. 換號（不論驗過沒）→ 舊號碼的倒數、延後補寄、錯誤全部作廢，回到確認段。
  //  3. 編輯中按了「傳送驗證碼」→ 呼叫端換好號碼了，現在才寄到新號碼。
  // 「從沒有號碼變成有號碼」不是換號：帳號手機是掛載後才補進來的。
  useEffect(() => {
    if (!phone) return;
    const prev = lastPhone.current;
    lastPhone.current = phone;
    if (verifiedPhone.current === null) {
      if (verified) verifiedPhone.current = phone;
    } else if (verifiedPhone.current !== phone) {
      verifiedPhone.current = null;
      verifiedHere.current = false;
      setVerified(false);
      setOtpVerified(false);
      setPhoneVerifiedTicket("");
    }
    if (prev && prev !== phone) {
      deferredPhone.current = null;
      setDeferredSecs(0);
      setSecs(0);
      setError("");
      setPhase("confirm");
    }
    if (pendingSendTo.current === phone) {
      pendingSendTo.current = null;
      startSend(phone, pendingChannel.current);
    }
    // eslint-disable-next-line
  }, [phone, verified, setOtpVerified, setPhoneVerifiedTicket]);

  // 外部清掉 otpVerified（true → false）而本卡還顯示「驗證成功」→ 回到確認段重新驗證。
  useEffect(() => {
    const was = prevStoreOtpVerified.current;
    prevStoreOtpVerified.current = storeOtpVerified;
    if (!was || storeOtpVerified || !verified) return;
    verifiedPhone.current = null;
    verifiedHere.current = false;
    setVerified(false);
    setCode(EMPTY_CODE);
    triedCode.current = null;
    setPhase("confirm");
  }, [storeOtpVerified, verified, setCode]);

  // 號碼不是手機（市話、非數字垃圾）→ 直接開「修改號碼」（不可能寄到那支）
  useEffect(() => {
    if (mode !== "edit" || verified || !onChangePhone) return;
    setDraftPhone("");
    setDraftError("");
    autoEditRef.current = true;
    setEditing(true);
    setPhase("confirm");
    // eslint-disable-next-line
  }, [mode, verified]);

  // 帳號手機晚到：號碼補成手機 → 關掉卡片自己打開的編輯框（使用者主動按的修改不關）
  useEffect(() => {
    if (mode !== "send" || !autoEditRef.current) return;
    autoEditRef.current = false;
    setEditing(false);
  }, [mode]);

  // 進入輸入驗證碼段時 focus 第一個空格
  useEffect(() => {
    if (phase !== "code" || verified) return;
    const t = setTimeout(() => {
      const firstEmpty = code.findIndex((c) => !c);
      inputs.current[firstEmpty === -1 ? 0 : firstEmpty]?.focus();
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [phase]);

  useEffect(() => {
    if (secs <= 0 || verified) return;
    const t = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [secs, verified]);

  // 延後寄送倒數；到 0 時若號碼沒換、還沒驗證 → 補寄一次
  useEffect(() => {
    if (deferredSecs > 0) {
      if (verified) return;
      const t = setInterval(() => setDeferredSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
      return () => clearInterval(t);
    }
    const target = deferredPhone.current;
    if (!target) return;
    deferredPhone.current = null;
    if (target === phone && !verified) void sendOtp(target, false, "sms", true);
    // eslint-disable-next-line
  }, [deferredSecs, verified, phone]);

  // 語音冷卻倒數（與簡訊各自獨立）
  useEffect(() => {
    if (voiceSecs <= 0 || verified) return;
    const t = setInterval(() => setVoiceSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [voiceSecs, verified]);

  // 填滿 5 碼 → 向後端驗證；碼被改動則退回未驗證。
  // triedCode 防止 verifying 回 false 時同一組碼被立刻重打；碼被改動、碼錯清空、或要重送時才清掉
  // （所以碼錯後重打同一組碼會再送；網路錯誤時那組碼根本沒送到後端）。
  useEffect(() => {
    const full = code.join("").length === 5;
    if (!full) {
      autoRetried.current = false;
      triedCode.current = null;
      // 只有「在這一頁驗證成功」的才會因為改動輸入碼而退回未驗證（alreadyVerified 不適用）。
      if (verified && verifiedHere.current) setVerified(false);
      return;
    }
    const otpValue = code.join("");
    if (verified || verifying || triedCode.current === otpValue) return;
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    triedCode.current = otpValue;
    setVerifying(true);
    setError("");
    handleRequest({
      asyncRequest: () => api.post("/app-api/line/verify-phone-otp", { accessToken: lineAccessToken, phone, otpCode: otpValue }, { isAuth: false }),
      successCallback: (res: AxiosResponse) => {
        const ticket = res?.data?.data?.phoneVerifiedTicket || "";
        setPhoneVerifiedTicket(ticket);
        setOtpVerified(true);
        verifiedHere.current = true;
        setVerified(true);
        onVerifiedRef.current?.(phone, ticket);
      },
      errorCallback: (e: unknown) => {
        if (isTransientError(e)) {
          // 網路錯誤：保留輸入，自動重送一次；再失敗才顯示網路訊息（不清空碼）。
          // triedCode 這裡先不清（否則 verifying 一回 false 就立刻重打、最終無限重送），由重送觸發時清。
          if (!autoRetried.current) {
            autoRetried.current = true;
            setTimeout(() => {
              triedCode.current = null;
              setRetryTick((t) => t + 1);
            }, 800);
            return;
          }
          setError(NETWORK_MSG);
          return;
        }
        triedCode.current = null;
        setError(getVerifyOtpErrorMessage(e));
        setCode([...EMPTY_CODE]);
        setTimeout(() => inputs.current[0]?.focus(), 0);
      },
      finalCallback: () => setVerifying(false),
    });
  }, [code, verified, verifying, phone, lineAccessToken, retryTick]);

  const digits = (phone || "").replace(/\D/g, "");
  const masked = phone ? phone.replace(/(\d{4})\d{3}(\d{3})/, "$1＊＊＊$2") : "";

  /** 從第 start 格起依序填入多個數字（簡訊自動填入/貼上整組驗證碼會一次給整串） */
  const fillFrom = (start: number, raw: string) => {
    const ds = raw.replace(/\D/g, "").split("");
    if (ds.length === 0) return;
    const n = [...code];
    ds.slice(0, code.length - start).forEach((d, k) => {
      n[start + k] = d;
    });
    setCode(n);
    const lastFilled = Math.min(start + ds.length, code.length) - 1;
    inputs.current[Math.min(lastFilled + 1, code.length - 1)]?.focus();
  };
  const setDigit = (i: number, v: string) => {
    if (verifying) return;
    if (v.replace(/\D/g, "").length > 1) {
      fillFrom(i, v);
      return;
    }
    if (!/^\d?$/.test(v)) return;
    const n = [...code];
    n[i] = v;
    setCode(n);
    if (v && i < code.length - 1) inputs.current[i + 1]?.focus();
  };
  const onKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  /** 讀編輯框：合法回傳純數字，否則顯示錯誤並回傳 null。 */
  const readDraft = (): string | null => {
    const next = draftPhone.replace(/\D/g, "");
    if (!isMobilePhone(next)) {
      setDraftError("請輸入 09 開頭的手機號碼");
      return null;
    }
    setDraftError("");
    return next;
  };
  /** 套用新號碼。回傳 true 表示號碼有換（要等呼叫端更新 phone）。 */
  const applyNewPhone = (next: string): boolean => {
    autoEditRef.current = false;
    setEditing(false);
    if (next === digits || !onChangePhone) return false;
    changedHere.current = true;
    onChangePhone(next);
    return true;
  };
  /** 編輯框的「確定」（或 Enter）：只換號、回到確認段，不寄簡訊。 */
  const confirmNewPhone = () => {
    const next = readDraft();
    if (next) applyNewPhone(next);
  };
  /** 打開「修改號碼」：回到確認段，取消舊號碼的延後補寄。 */
  const openEdit = () => {
    deferredPhone.current = null;
    setDeferredSecs(0);
    setDraftPhone("");
    setDraftError("");
    autoEditRef.current = false;
    setEditing(true);
    setPhase("confirm");
  };
  /** 「傳送驗證碼」：沒勾協議不寄；編輯中先套用新號碼。兩條路都在這次點擊內切畫面並 focus 第一格。 */
  const onPressSend = (channel: "sms" | "voice" = "sms") => {
    if (!agreed) {
      setAgreeError(true);
      return;
    }
    if (editing) {
      const next = readDraft();
      if (!next) return;
      if (next !== digits && onChangePhone) {
        // 號碼有換：寄送仍交給換號 effect（等呼叫端把 phone 換成這支，見 pendingSendTo），
        // 但「切到輸入段」不能等 effect——effect 內的 setState 要等下一輪才畫出來，
        // 輸入格不在畫面上就 focus 不到。所以換號與切畫面一起在 flushSync 內做完，再立刻 focus。
        pendingSendTo.current = next;
        pendingChannel.current = channel;
        flushSync(() => {
          applyNewPhone(next);
          showCodePhase(next, channel);
        });
        focusFirstCodeInput();
        return;
      }
      applyNewPhone(next); // 號碼沒換：只關編輯框
      startSendFromTap(next, channel);
      return;
    }
    if (!isMobilePhone(phone)) return;
    startSendFromTap(phone, channel);
  };
  const canPressSend = agreed && (editing || isMobilePhone(phone));

  return (
    <>
      <SectionLabel>簡訊驗證</SectionLabel>
      <Card>
        {verified ? (
          <XStack ai="center" gap={12} py={8}>
            <YStack className="kiosk-ringpop" ai="center" jc="center" width={34} height={34} borderRadius={17} backgroundColor="#10B981">
              <Text fontSize={18} fontWeight="900" color="#fff">
                ✓
              </Text>
            </YStack>
            <YStack>
              <Text fontSize={14.5} fontWeight="800" color="#15803D">
                驗證成功
              </Text>
              <Text fontSize={12.5} color={kiosk.grey} mt={1}>
                已完成 {masked || "門號"} 的身分驗證
              </Text>
              {!!verifiedNote && (
                <Text fontSize={12.5} color="#B45309" mt={2}>
                  {verifiedNote}
                </Text>
              )}
            </YStack>
          </XStack>
        ) : phase === "confirm" ? (
          <YStack py={4} gap={12}>
            <Text fontSize={13} color={kiosk.grey}>
              {editing && noAccountPhone
                ? "首次下單需驗證手機，請輸入您的手機號碼"
                : editing && mode === "edit"
                ? "帳號上的號碼不是手機，請輸入要接收驗證碼的手機號碼" : editing ? "輸入要驗證的手機號碼" : "首次下單需驗證手機，驗證碼會以簡訊傳送到："}
            </Text>
            {/* 設計稿 ②：確認段號碼、修改號碼、沒手機輸入框上方都有必填標籤 */}
            <Text fontSize={13} fontWeight="700" color={kiosk.ink} mb={-6}>
              手機號碼 <Text color={kiosk.danger}>*</Text>
            </Text>
            {editing ? (
              <YStack gap={8}>
                <input
                  value={draftPhone}
                  inputMode="numeric"
                  placeholder={noAccountPhone ? "請輸入手機號碼（09 開頭）" : "0912345678"}
                  onChange={(e) => setDraftPhone(e.target.value)}
                  // 整頁在 <Form> 內：Enter 會隱式送出訂單，改成等同按「確定」（不寄簡訊）
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      confirmNewPhone();
                    }
                  }}
                  style={{ height: 46, fontSize: 18, fontWeight: 700, padding: "0 12px", border: `1.5px solid ${draftError ? kiosk.danger : kiosk.blue}`, borderRadius: 12, outline: "none", boxSizing: "border-box", width: "100%" }}
                />
                {!!draftError && (
                  <Text fontSize={13} color={kiosk.danger} fontWeight="600">
                    {draftError}
                  </Text>
                )}
                <XStack ai="center" gap={16}>
                  {/* 整頁包在 <Form> 內，按鈕一律 type="button" 避免誤送出訂單 */}
                  <button
                    type="button"
                    onClick={confirmNewPhone}
                    style={{ height: 36, padding: "0 18px", borderRadius: 10, border: `1.5px solid ${kiosk.blue}`, background: "#fff", color: kiosk.blue, fontSize: 14, fontWeight: 700 }}
                  >
                    確定
                  </button>
                  {/* 原號碼不是手機（或帳號沒手機）時沒有「改回」可言，不給取消 */}
                  {mode !== "edit" && !noAccountPhone && (
                    <button type="button" onClick={() => setEditing(false)} style={{ background: "none", border: "none", color: kiosk.grey, fontSize: 13 }}>
                      取消，改回 {formatMobile(digits)}
                    </button>
                  )}
                </XStack>
              </YStack>
            ) : (
              <XStack ai="center" jc="space-between">
                <Text fontSize={20} fontWeight="800" color={kiosk.ink} letterSpacing={0.5}>
                  {digits ? formatMobile(digits) : "讀取帳號手機中…"}
                </Text>
                {onChangePhone && (
                  <Text fontSize={13.5} color={kiosk.blue} fontWeight="600" cursor="pointer" onPress={openEdit}>
                    修改號碼
                  </Text>
                )}
              </XStack>
            )}
            <CheckoutAgreement
              checked={agreed}
              onChange={(v) => {
                setAgreed(v);
                if (v) setAgreeError(false);
              }}
              showError={agreeError}
            />
            {/* 不用 disabled：沒勾時要能按、並在勾選框下顯示紅字提示（否則客戶不知道為什麼按不了） */}
            <button
              type="button"
              aria-disabled={!canPressSend}
              onClick={() => onPressSend("sms")}
              style={{ height: 46, borderRadius: 12, border: "none", background: canPressSend ? kiosk.blue : "#CBD5E1", color: "#fff", fontSize: 15.5, fontWeight: 700 }}
            >
              傳送簡訊驗證碼
            </button>
            {/* 次要按鈕：與簡訊同規則（要勾協議、編輯中先換號），改以電話語音寄出 */}
            <button
              type="button"
              aria-disabled={!canPressSend}
              onClick={() => onPressSend("voice")}
              style={{ height: 46, borderRadius: 12, border: `1.5px solid ${kiosk.blue}`, background: "#fff", color: kiosk.blue, fontSize: 15.5, fontWeight: 700 }}
            >
              改用電話語音接收
            </button>
            <Text fontSize={12.5} color={kiosk.grey} ta="center">
              選電話：{VOICE_OTP_CALLER_ID} 會打給您，唸兩次驗證碼
            </Text>
          </YStack>
        ) : (
          <YStack py={4} gap={14}>
            <Text fontSize={13} color={kiosk.grey}>
              {deferredSecs > 0
                ? `首次下單需驗證身分，${deferredSecs} 秒後寄送驗證碼到${changedHere.current ? "新號碼 " : " "}${masked || "您的門號"}`
                : lastChannel === "voice" && voiceFailed
                ? "首次下單需驗證身分，電話語音未能撥出"
                : lastChannel === "voice"
                ? `首次下單需驗證身分，${VOICE_OTP_CALLER_ID} 將撥打電話至 ${masked || "您的門號"}，會唸兩次驗證碼`
                : `首次下單需驗證身分，驗證碼已傳送至 ${masked || "您的門號"}`}
              {onChangePhone && !verifying && (
                <Text fontSize={13} color={kiosk.blue} fontWeight="600" cursor="pointer" onPress={openEdit}>
                  {" "}
                  修改號碼
                </Text>
              )}
            </Text>

            <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
              {code.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputs.current[i] = el;
                  }}
                  value={d}
                  inputMode="numeric"
                  // 簡訊驗證碼自動填入：只掛第一格；不可設 maxLength=1（會把整組截成單碼）
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  name={i === 0 ? "one-time-code" : undefined}
                  disabled={verifying}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onPaste={(e) => {
                    e.preventDefault();
                    fillFrom(i, e.clipboardData.getData("text"));
                  }}
                  onKeyDown={(e) => onKey(i, e)}
                  style={{
                    width: 46,
                    height: 54,
                    textAlign: "center",
                    fontSize: 22,
                    fontWeight: 700,
                    color: kiosk.ink,
                    border: `1.5px solid ${d ? kiosk.blue : kiosk.line}`,
                    borderRadius: 12,
                    outline: "none",
                    background: d ? "#EFF6FF" : "#fff",
                    boxSizing: "border-box",
                    opacity: verifying ? 0.6 : 1,
                  }}
                />
              ))}
            </div>

            {/* 錯誤訊息與「倒數／重新傳送」分開兩行，避免有錯誤時重送鍵永遠出不來 */}
            {!!error && (
              <Text fontSize={13} color={kiosk.danger} fontWeight="600">
                {error}
              </Text>
            )}
            {/* 網路錯誤不清空碼，使用者沒辦法「重打」觸發驗證 → 給一個手動重送 */}
            {error === NETWORK_MSG && !verifying && code.join("").length === 5 && (
              <Text
                fontSize={13}
                color={kiosk.blue}
                fontWeight="600"
                cursor="pointer"
                onPress={() => {
                  autoRetried.current = false;
                  triedCode.current = null;
                  setRetryTick((t) => t + 1);
                }}
              >
                重新驗證
              </Text>
            )}
            {verifying ? (
              <Text fontSize={13} color={kiosk.grey}>
                驗證中…
              </Text>
            ) : (
              <YStack gap={6}>
                {deferredSecs > 0 ? null : secs > 0 ? (
                  <Text fontSize={13} color={kiosk.grey}>
                    {secs} 秒後可{lastChannel === "voice" ? "改用簡訊接收" : "重新傳送"}
                  </Text>
                ) : lastChannel === "voice" ? (
                  // 語音寄出後的簡訊入口：沿用簡訊冷卻與 429 延後補寄（非 manual）
                  <Text fontSize={13} color={kiosk.blue} fontWeight="600" cursor="pointer" onPress={() => sendOtp(phone)}>
                    改用簡訊接收
                  </Text>
                ) : (
                  <Text fontSize={13} color={kiosk.blue} fontWeight="600" cursor="pointer" onPress={() => sendOtp(phone, true)}>
                    重新傳送驗證碼
                  </Text>
                )}
                {/* 收不到簡訊的救援管道：改由系統打電話唸驗證碼 */}
                <XStack height={1} backgroundColor={kiosk.line} width="55%" my={2} />
                {voiceSending ? (
                  <Text fontSize={12.5} color={kiosk.grey}>
                    電話撥打中，來電號碼 {VOICE_OTP_CALLER_ID}
                  </Text>
                ) : voiceSecs > 0 ? (
                  <Text fontSize={12.5} color="#CBD5E1">
                    語音驗證 {voiceSecs} 秒後可再試
                  </Text>
                ) : lastChannel === "voice" ? (
                  <Text fontSize={12.5} color={kiosk.blue} cursor="pointer" onPress={() => void sendVoiceOtp()}>
                    沒接到電話？重新撥打
                  </Text>
                ) : (
                  <Text fontSize={12.5} color={kiosk.blue} cursor="pointer" onPress={() => void sendVoiceOtp()}>
                    收不到簡訊？改用電話語音接收
                  </Text>
                )}
              </YStack>
            )}
          </YStack>
        )}
      </Card>
    </>
  );
};

export default OtpCard;
